import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { CheckCircleRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PreviewBox, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import { bandCheck, bandFieldErrors, bandsWithTo, emptyBand, nextDeductionMonth } from "./lwfConfigData";
import { checkLwfBands, testLwfEmployee } from "./lwfConfigApi";

// Edit Band drawer (LWF1b): wage from, the amounts of the chosen pattern,
// the gap check over all bands (POST config/bands/check/) and Test on
// employee (POST config/test-employee/ with the unsaved rule; saves
// nothing). "Wage to" is worked out from the next band (read only).

const SERVER_DELAY = 600;

export const LwfBandPanel = ({ open, band, form, siteName, readOnly, domain, companyId, siteId, employees, onClose, onSave, onDelete }) => {
  const adding = !band;
  const [draft, setDraft] = useState(() => band || emptyBand());
  const [showErrors, setShowErrors] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [serverCheck, setServerCheck] = useState(null);
  const [test, setTest] = useState({ loading: false, error: "", data: null });
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(band ? { ...band } : emptyBand());
    setShowErrors(false);
    setServerCheck(null);
    setTest({ loading: false, error: "", data: null });
  }, [open, band]);

  // First employee once the list arrives.
  useEffect(() => {
    if (open && !employeeId && employees?.length) setEmployeeId(employees[0].id);
  }, [open, employeeId, employees]);

  const percent = form.amount_type === "PERCENT_CAP";
  const set = (key) => (value) => setDraft((current) => ({ ...current, [key]: value }));
  const others = form.bands.filter((item) => item.id !== draft.id);
  const merged = [...others, draft];
  const errors = bandFieldErrors(draft, form.amount_type);
  if (!errors.wage_from && others.some((item) => Number(item.wage_from) === Number(draft.wage_from))) {
    errors.wage_from = "Another band starts at this wage";
  }
  const error = (key) => (showErrors ? errors[key] : undefined);
  const localCheck = bandCheck(merged, form.amount_type);
  const wageTo = bandsWithTo(merged).find((item) => item.id === draft.id)?.wage_to;
  const complete = !Object.keys(errors).length;
  const month = (nextDeductionMonth(form) || moment()).format("YYYY-MM");

  // The server's gap check once the bands pass the same check here.
  const bandsKey = JSON.stringify({ type: form.amount_type, bands: merged });
  useEffect(() => {
    if (!open || !localCheck.valid) {
      setServerCheck(null);
      return undefined;
    }
    const request = new AbortController();
    const timer = setTimeout(() => {
      const { type, bands } = JSON.parse(bandsKey);
      checkLwfBands({ domain, companyId, amountType: type, bands, signal: request.signal })
        .then((data) => setServerCheck({ ok: Boolean(data?.ok), message: data?.message || errorsText(data?.errors) }))
        .catch((err) => {
          if (err.name !== "AbortError") setServerCheck({ ok: false, message: err.message });
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, localCheck.valid, bandsKey, domain, companyId]);

  // Test on employee with the unsaved rule (this band included).
  const formKey = JSON.stringify({ ...form, bands: merged });
  useEffect(() => {
    if (!open || !complete || !employeeId) {
      setTest({ loading: false, error: "", data: null });
      return undefined;
    }
    const request = new AbortController();
    setTest((current) => ({ ...current, loading: true, error: "" }));
    const timer = setTimeout(() => {
      testLwfEmployee({ domain, companyId, siteId, employeeId, month, form: JSON.parse(formKey), signal: request.signal })
        .then((data) => setTest({ loading: false, error: "", data }))
        .catch((err) => {
          if (err.name !== "AbortError") setTest({ loading: false, error: err.message, data: null });
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, complete, employeeId, month, formKey, domain, companyId, siteId]);

  const check = !localCheck.valid ? { valid: false, message: localCheck.message } : serverCheck ? { valid: serverCheck.ok, message: serverCheck.message } : localCheck;

  const save = () => {
    setShowErrors(true);
    if (!complete) return;
    onSave(draft);
  };

  const amountInput = (key, label, helper, adornment = "₹", end) => (
    <Field label={<>{label}<Required /></>} helper={helper} error={error(key)}>
      <PlainInput
        type="number"
        adornment={adornment}
        endAdornment={end}
        value={draft[key]}
        onChange={set(key)}
        disabled={readOnly}
        error={error(key)}
        inputProps={{ min: 0, step: "any" }}
      />
    </Field>
  );

  const result = test.data;
  const employeeOptions = (employees || []).map((item) => ({ value: item.id, label: [item.name, item.code].filter(Boolean).join(" · ") }));

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={adding ? "Add Band" : readOnly ? "Band" : "Edit Band"}
      subtitle={`${siteName} · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        readOnly ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ width: "100%" }}>
            {!adding && onDelete ? (
              <Mui.Button onClick={() => setDeleteOpen(true)} sx={{ color: "#DC2626", textTransform: "none", fontWeight: designWeights.semibold, mr: { sm: "auto" } }}>
                Delete band
              </Mui.Button>
            ) : null}
            <Mui.Box sx={{ flex: 1 }}>
              <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Band" : "Save"} primaryProps={{ onClick: save }} />
            </Mui.Box>
          </Mui.Stack>
        )
      }
    >
      <Mui.Stack spacing={2}>
        {readOnly ? <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change bands." /> : null}

        <FormSection title="Wage range" info="Leave “Wage to” for the top band (and above). It is the next band's wage from − 1.">
          {amountInput("wage_from", "Wage from", "Lowest monthly LWF wage in this band. The first band starts at ₹0.")}
          <Field label="Wage to" helper="Worked out: the next band's wage from − 1.">
            <PlainInput
              adornment="₹"
              value={wageTo === "" || wageTo == null ? "" : formatInr(wageTo)}
              placeholder="and above"
              onChange={() => {}}
              disabled
            />
          </Field>
        </FormSection>

        <FormSection title="Amounts" info={percent ? "Employee pays a % of the LWF wage up to a maximum; employer pays a multiple of that, up to its own maximum." : "Fixed amounts for every employee in this band."}>
          {percent ? (
            <>
              {amountInput("employee_pct", "Employee pays", "Percentage of the LWF wage.", undefined, "%")}
              {amountInput("employee_max", "Employee maximum", "The employee share never goes above this.")}
              {amountInput("employer_multiple", "Employer pays", "Times the employee share (e.g. 2 = twice).", undefined, "× employee")}
              {amountInput("employer_max", "Employer maximum", "The employer share never goes above this.")}
            </>
          ) : (
            <>
              {amountInput("employee_amount", "Employee pays", "Deducted from pay in each deduction month.")}
              {amountInput("employer_amount", "Employer pays", "Company cost, paid to the Board with the employee share.")}
            </>
          )}
        </FormSection>

        <FormSection title="Check">
          <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ gridColumn: "1 / -1" }}>
            {check.valid ? <CheckCircleRounded sx={{ fontSize: 16, color: "#12B76A" }} /> : <ErrorOutlineRounded sx={{ fontSize: 16, color: "#DC2626" }} />}
            <Mui.Typography sx={{ ...designType.captionText, color: check.valid ? "#027A48" : "#DC2626" }}>
              {String(check.message || "").replace(/^✓\s*/, "")}
            </Mui.Typography>
          </Mui.Stack>
        </FormSection>

        <FormSection title="Test on employee" info={`Preview for ${moment(`${month}-01`).format("MMM YYYY")} — nothing is saved.`}>
          <Field wide label="Employee" helper="Shows what this band gives the employee.">
            <SelectField
              value={employeeId}
              onChange={(value) => value && setEmployeeId(value)}
              options={employeeOptions}
              placeholder={employees ? "Pick an employee" : "Loading employees…"}
              disabled={!employees}
            />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <PreviewBox>
              {!complete ? (
                <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>Fill the fields above to test this band.</Mui.Typography>
              ) : test.loading ? (
                <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>Working it out…</Mui.Typography>
              ) : test.error ? (
                <Mui.Typography sx={{ ...designType.helperText, color: "#DC2626" }}>{test.error}</Mui.Typography>
              ) : result ? (
                <Mui.Stack spacing={0.5}>
                  {result.note ? <Mui.Typography sx={designType.captionText}>{result.note}</Mui.Typography> : null}
                  {(result.steps || []).map((step) => (
                    <Mui.Typography key={step} sx={designType.captionText}>
                      {step}
                    </Mui.Typography>
                  ))}
                  <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate900 }}>
                    {result.sentence || result.result?.label || "—"}
                  </Mui.Typography>
                  {(result.warnings || []).map((warning) => (
                    <Mui.Typography key={warning} sx={{ ...designType.captionText, color: "#B54708" }}>
                      {warning}
                    </Mui.Typography>
                  ))}
                </Mui.Stack>
              ) : (
                <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>
                  {employees && !employees.length ? "No employees to test on." : "Pick an employee to test this band."}
                </Mui.Typography>
              )}
            </PreviewBox>
          </Mui.Box>
        </FormSection>

        {!percent ? (
          <RequestInfoBanner body="For percentage states (for example Haryana) choose Amount type “Percentage of wage with a maximum” — the band then asks for employee % and maximum ₹, and employer as a multiple of the employee share." />
        ) : null}
      </Mui.Stack>
      <GlobalConfirmDialog
        open={deleteOpen}
        title="Delete band"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          onDelete(band);
        }}
        confirmLabel="Delete"
        cancelLabel="Keep"
        maxWidth="sm"
        danger
      >
        Delete the band from ₹{formatInr(band?.wage_from || 0)}? The bands are saved when you save the version.
      </GlobalConfirmDialog>
    </GlobalSidePanel>
  );
};

// { "bands.1.wage_from": ["…"] } -> "…"
function errorsText(errors) {
  if (!errors || typeof errors !== "object") return "";
  return Object.values(errors)
    .flat()
    .map((message) => String(message))
    .join(" ");
}
